import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import "./sidebar.css";
import Logo from "../../assets/logo-text.svg";
import { useRole } from "../../context/RoleContext";
import { PAGES_ITENS } from "../../utils/pagesMapper";
import logoSidebar from "../../assets/logo-sidebar.svg";
import accessValidation from "../../utils/accessValidation";
import hamburgerIcon from "../../assets/icons/hamburguerIcon.svg";
import { useInstitution } from "../../context/InstitutionContext";
import { useMantenedora } from "../../context/MantenedoraContext";
import chevronDownIcon from "../../assets/icons/arrow-down.svg";
import { getCookie } from "../../utils/cookies";
import { readCustomizerDashboardByUser } from "../../services/customDashboard";
import { getFavoriteDashboard } from "../../services/preferences";
import MiniDashboardIcon from "../../assets/icons/dashboard-icon.svg";
import { getNotifications } from "../../services/notifications";

const Sidebar = ({ toggleCollapse, isCollapsed }) => {
  const { role } = useRole();
  const location = useLocation();
  const { institution } = useInstitution();
  const { mantenedora } = useMantenedora();
  const [expandedDropdowns, setExpandedDropdowns] = useState({});
  const [customDashboards, setCustomDashboards] = useState([]);
  const [favoriteDashboardId, setFavoriteDashboardId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!institution?.id) {
      return;
    }
    const fetchUnread = async () => {
      try {
        const data = await getNotifications(institution.id, { limit: 1 });
        setUnreadCount(data?.unreadCount || 0);
      } catch {
        // silencioso: contador é informativo
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [institution?.id, location.pathname]);

  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        const token = getCookie("token");
        if (!token) return;

        const decoded = jwtDecode(token);
        const userId = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] 
          || decoded["sub"] 
          || decoded["userId"]
          || decoded["nameid"];

        if (!userId || !institution?.id) return;

        const [dashboardsData, favoriteId] = await Promise.all([
          readCustomizerDashboardByUser(userId, institution.id),
          getFavoriteDashboard()
        ]);

        if (dashboardsData) {
          const dashboards = Array.isArray(dashboardsData) ? dashboardsData : [dashboardsData];
          setCustomDashboards(dashboards);
        }

        if (favoriteId) {
          setFavoriteDashboardId(String(favoriteId));
        }
      } catch (error) {
        console.error("Erro ao buscar dashboards para a sidebar:", error);
      }
    };

    fetchDashboards();
  }, [institution]);

  const checkDisabled = (path) => {
    const { isFreeFlow, route } = accessValidation({ 
      institution, 
      role, 
      hasMantenedora: !!mantenedora?.id 
    });

    return !isFreeFlow && route !== path;
  };

  const toggleDropdown = (itemTitle) => {
    setExpandedDropdowns(prev => ({
      ...prev,
      [itemTitle]: !prev[itemTitle]
    }));
  };

  const isDropdownExpanded = (itemTitle) => {
    return expandedDropdowns[itemTitle] || false;
  };

  const isActiveDropdown = (item) => {
    if (item.hasDropdown && item.subItems) {
      return item.subItems.some(subItem => location.pathname === subItem.path);
    }
    return location.pathname === item.path;
  };

  const renderMenuItem = (item, index) => {
    // Tratamento especial para Dashboard Customizado
    if (item.title === "Dashboard Customizado" && customDashboards.length > 0) {
      const sortedDashboards = [...customDashboards].sort((a, b) => {
        const idA = String(a.dashboardId || a.id);
        const idB = String(b.dashboardId || b.id);
        if (idA === favoriteDashboardId) return -1;
        if (idB === favoriteDashboardId) return 1;
        return 0;
      });

      const dashboardItem = {
        ...item,
        hasDropdown: true,
        subItems: [
          {
            title: "Ver todos",
            icon: item.icon,
            path: "/app/dashboard-customizado",
            className: item.className
          },
          ...sortedDashboards.map(db => ({
            title: db.name,
            icon: MiniDashboardIcon,
            path: `/app/dashboard-customizado/${db.dashboardId || db.id}`,
            className: "custom-dashboard-subitem"
          }))
        ]
      };
      
      return renderDropdownItem(dashboardItem, index);
    }

    if (item.hasDropdown && item.subItems) {
      return renderDropdownItem(item, index);
    }

    return (
      <Link
        to={item.path}
        key={index}
        className={clsx(item.className, { disabled: checkDisabled(item.path) })}
      >
        <li
          className={clsx({
            active: location.pathname === item.path,
            collapsed: isCollapsed
          })}
        >
          <img src={item.icon} alt={item.title} className="icon" />
          {!isCollapsed && item.title}
          {item.path === "/app/notificacoes" && unreadCount > 0 && (
            <span className="sidebar-badge">
              {isCollapsed ? "" : unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </li>
      </Link>
    );
  };

  const renderDropdownItem = (item, index) => {
    return (
      <div key={index} className="dropdown-container">
        <div
          className={clsx(
            "dropdown-header",
            item.className,
            { 
              disabled: checkDisabled(item.path),
              active: isActiveDropdown(item),
              collapsed: isCollapsed
            }
          )}
        >
          <Link to={item.path} className="dropdown-link-wrapper">
            <img src={item.icon} alt={item.title} className="icon" />
            {!isCollapsed && <span>{item.title}</span>}
          </Link>
          {!isCollapsed && (
            <img 
              src={chevronDownIcon} 
              alt="Dropdown" 
              className={clsx("dropdown-arrow", {
                expanded: isDropdownExpanded(item.title)
              })}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDropdown(item.title);
              }}
            />
          )}
        </div>
        {!isCollapsed && isDropdownExpanded(item.title) && (
          <div className="dropdown-content">
            {item.subItems.map((subItem, subIndex) => (
              <Link
                to={subItem.path}
                key={subIndex}
                className={clsx(
                  "dropdown-item",
                  subItem.className,
                  { disabled: checkDisabled(subItem.path) }
                )}
              >
                <div
                  className={clsx({
                    active: location.pathname === subItem.path
                  })}
                >
                  <img src={subItem.icon} alt={subItem.title} className="icon" />
                  <span>{subItem.title}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={clsx("sidebar", isCollapsed && "sidebarCollapsed")}>
      <div className="logo-container">
        {!isCollapsed ? (
          <img src={logoSidebar} alt="Academetrics Logo" className="logo-img" />
        ) : (
          <img src={Logo} alt="Academetrics Logo" className="logo-img" onClick={toggleCollapse} />
        )}
      </div>
      <div className="sidebar-nav-wrapper">
        <ul className="sidebar-list">
          {!isCollapsed && <p className="section-title">INDICADORES</p>}

          {PAGES_ITENS.INDICATORS.map((item, index) => renderMenuItem(item, index))}
        </ul>

        <ul className="sidebar-list">
          {!isCollapsed && <p className="section-title">SISTEMA</p>}

          {PAGES_ITENS.SYSTEM.map((item, index) => renderMenuItem(item, index))}
        </ul>

        {/* Seção Mantenedora - visível para Admin/MantenedoraAdmin/MantenedoraManager que pertencem a uma mantenedora */}
        {(!!mantenedora?.id && PAGES_ITENS.MANTENEDORA.some(item =>
          !item.roles || item.roles.includes(role)
        )) && (
          <ul className="sidebar-list">
            {!isCollapsed && <p className="section-title">MANTENEDORA</p>}

            {PAGES_ITENS.MANTENEDORA
              .filter(item => !item.roles || item.roles.includes(role))
              .map((item, index) => renderMenuItem(item, index))}
          </ul>
        )}
      </div>
      <img
        src={hamburgerIcon}
        alt="Menu Icon"
        className={clsx("hamburger-icon", isCollapsed && "collapse")}
        onClick={toggleCollapse}
      />
    </div>
  );
};

export default Sidebar;
